import { ExportReadable, reportsExportsListOptions } from '@crm/api'
import {
  ConditionalText,
  DayLabel,
  TableActions,
  TableContainer,
  Text,
  UserAvatar
} from '@shared/components'
import { useLocales } from '@shared/locales'
import { defaultFilters, downloadFile, getOrderDirection } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { message, TableColumnsType, Tag } from 'antd'
import { MenuProps } from 'antd/lib'
import { DocumentDownload } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

type ExportsFilters = {
  page?: number
  ordering?: string
  status?: string
}

export const ProductsExports = () => {
  const { t } = useLocales()
  const [filters, setFilters] = useState<ExportsFilters>({ page: 1 })

  const { data, isLoading } = useQuery(
    reportsExportsListOptions({
      query: {
        ...defaultFilters,
        ...filters
      }
    })
  )

  const getActionColumnItem = useCallback(
    (exportFile: ExportReadable & { file?: string }): MenuProps['items'] => [
      {
        label: t('documents.download'),
        key: 'download',
        icon: <DocumentDownload size={20} />,
        onClick: () => {
          const fileUrl = exportFile.file || exportFile.attachment?.file

          if (!fileUrl) {
            message.error(t('documents.file_not_ready'))
            return
          }

          const filename = exportFile.attachment?.title
          downloadFile(fileUrl, filename)
        }
      }
    ],
    [t]
  )

  const columns: TableColumnsType<ExportReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.title'),
        key: 'attachment',
        dataIndex: 'attachment',
        render: (attachment) => (
          <ConditionalText condition={!!attachment?.title}>
            {attachment?.title}
          </ConditionalText>
        )
      },
      {
        title: t('format'),
        dataIndex: 'format',
        key: 'format',
        render: (format) => (
          <ConditionalText condition={!!format}>{format}</ConditionalText>
        )
      },

      {
        title: t('task.created_by'),
        key: 'created_by',
        sorter: true,
        render: (_, { created_by }) => <UserAvatar userData={created_by} />
      },
      {
        title: t('task.status'),
        dataIndex: 'status',
        key: 'status',
        render: (status) =>
          status ? (
            <Tag color={status === 'done' ? 'green' : 'blue'}>{status}</Tag>
          ) : (
            '-'
          )
      },
      {
        title: t('lead.created_at'),
        key: 'created_at',
        sorter: true,
        render: (_, { created_at }) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        key: 'modified_at',
        sorter: true,
        render: (_, { modified_at }) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: (file: ExportReadable & { file: string }) => (
          <TableActions options={getActionColumnItem(file)} />
        )
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <TableContainer
      rowKey="id"
      rowClassName="pointer"
      columns={columns}
      data={data}
      loading={isLoading}
      isHoverAlternative
      showSearch={false}
      showSorterTooltip={false}
      leftChildren={
        <Text weight={600} size={16}>
          {t('menu.exports')}
        </Text>
      }
      pagination={{
        hideOnSinglePage: true,
        defaultCurrent: filters.page,
        pageSize: 10,
        total: data?.count,
        showSizeChanger: false
      }}
      onChange={(pagination, _, sorter) => {
        if (pagination.current) {
          setFilters((filter) => ({ ...filter, page: pagination.current }))
        }

        const ordering = getOrderDirection(sorter)
        setFilters((filter) => ({ ...filter, ordering }))
      }}
    />
  )
}
